import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from './useDebounce';

interface StoredData<T> {
  source: 'usePersistedState';
  timestamp: number;
  ttl: number;
  data: {
    version: string;
    value: T;
  };
}

/**
 * Hook extends useState to persist state in localStorage
 * @param initialValue - The initial value of the state
 * @param opts - The options for the state
 * @returns The state and the setState function
 */
export function usePersistedState<T>(
  initialValue: T,
  optsOrKey: string | { key: string; ttl?: number; version?: string },
) {
  const opts = useMemo(
    () => (typeof optsOrKey === 'string' ? { key: optsOrKey } : optsOrKey),
    [optsOrKey],
  );

  // Initialize state by checking localStorage first
  const [state, setState] = useState<T>(() => {
    const persistedValue = loadData<T>({
      key: opts.key,
      version: opts.version,
    });
    return persistedValue !== null ? persistedValue : initialValue;
  });

  // debounce state to prevent frequent localStorage writes
  const stateDebounced = useDebounce(state, 300);

  // Save data when state changes
  useEffect(() => {
    saveData({ ...opts, data: stateDebounced });
  }, [stateDebounced, opts]);

  // Reset state and save immediately
  const reset = () => {
    setState(initialValue);
    saveData({ ...opts, data: initialValue });
  };

  return [state, setState, reset] as const; // Add const assertion for better type inference
}

/**
 * Saves data to localStorage with version and ttl
 * @param key - The key to save the data to
 * @param data - The data to save
 * @param ttl - The time to live in milliseconds
 * @param version - The version of the data
 */
function saveData<T>({
  key,
  data,
  ttl = 1000 * 60 * 60 * 24 * 7, // 1 week
  version = '1',
}: {
  key: string;
  data: T;
  ttl?: number;
  version?: string;
}) {
  console.log('persisted state, saveData', key, data);
  if (data === null || data === undefined) return;

  const storedData: StoredData<T> = {
    source: 'usePersistedState',
    timestamp: Date.now(), // Add timestamp for TTL calculation
    ttl,
    data: {
      version,
      value: data,
    },
  };

  try {
    localStorage.setItem(key, JSON.stringify(storedData));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
}
/**
 * Loads data from localStorage with respect to the version and ttl
 * @param key - The key to load the data from
 * @param version - The version of the data to load
 * @returns The data or null if it doesn't exist or is expired
 */
function loadData<T>({
  key,
  version = '1',
}: {
  key: string;
  version?: string;
}): T | null {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const val = JSON.parse(item) as StoredData<T>;

    if (val.source !== 'usePersistedState') {
      localStorage.removeItem(key);
      return null;
    }

    // Check if data has expired
    const hasExpired = Date.now() > val.timestamp + val.ttl;
    if (hasExpired) {
      localStorage.removeItem(key);
      return null;
    }

    // Check version match
    if (val.data.version !== version) {
      localStorage.removeItem(key);
      return null;
    }

    return val.data.value;
  } catch (error) {
    console.error('Failed to load from localStorage:', error);
    localStorage.removeItem(key);
    return null;
  }
}
